
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useImageLibrary } from '@/hooks/runware/image-library';
import { useUser } from '@/hooks/auth/use-user';
import { useDebouncedValue } from '@/lib/utils';

interface ImageLibraryFilter {
  styleId?: string;
  influenceId?: string;
  backgroundId?: string;
  environmentIds?: string[];
  moodIds?: string[];
  colorIds?: string[];
  imageType?: string;
  description?: string;
  width?: number;
  height?: number;
}

export const useImageLibraryData = (
  filters: ImageLibraryFilter,
  showOnlyUserImages: boolean,
  showFavorites: boolean
) => {
  const { user } = useUser();
  
  // Use debounced description internally to prevent premature loading
  const debouncedDescription = useDebouncedValue(filters.description || '', 500);
  
  const { 
    useImageLibraryQuery, 
    deleteImage,
    setSearchTerm: librarySetSearchTerm 
  } = useImageLibrary();

  // Update search term only when debounced description changes
  useEffect(() => {
    librarySetSearchTerm(debouncedDescription);
  }, [debouncedDescription, librarySetSearchTerm]);

  // Parse width and height to integers if they exist
  const parsedWidth = filters.width ? parseInt(String(filters.width)) : undefined;
  const parsedHeight = filters.height ? parseInt(String(filters.height)) : undefined;
  
  // Create the filter object using debounced description
  const apiFilters = useMemo(() => ({
    styleId: filters.styleId,
    influenceId: filters.influenceId,
    backgroundId: filters.backgroundId,
    environmentIds: filters.environmentIds,
    moodIds: filters.moodIds,
    colorIds: filters.colorIds,
    imageType: filters.imageType,
    description: debouncedDescription, // Use debounced description here
    width: parsedWidth,
    height: parsedHeight,
    userId: showOnlyUserImages && user ? user.id : undefined,
    showFavorites: showFavorites
  }), [filters, debouncedDescription, parsedWidth, parsedHeight, showOnlyUserImages, showFavorites, user]);

  // Query for images with filters and pagination
  const {
    flatData: images,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useImageLibraryQuery(apiFilters);

  // Function to handle deleting images
  const handleDeleteImage = useCallback((id: string) => {
    deleteImage.mutate(id);
  }, [deleteImage]);

  return {
    images,
    isLoading,
    isError,
    loadMore: fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    handleDeleteImage,
    searchTerm: debouncedDescription,
    setSearchTerm: () => {} // No-op since we handle debouncing internally
  };
};
